import './table-admin.scss';
import React, {useEffect, useState} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Grid from '@mui/material/Grid2';
import {useTranslation} from 'react-i18next';
import apiClient from '../../../../utils/apiClient';
import decodeToken from '../../../../utils/decode-token';
import {Alert, Snackbar} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import SortableHeaderCell from '../../common/table/sortable-header-cell';
import RowActionsMenu from '../../common/table/row-actions-menu';
import TableResultsSummary from '../../common/table/table-results-summary';
import handleEmptyTableResponse from '../../common/table/utils/handle-empty-table-response';
import {
  compareText,
  countryLabelOptions,
  getNextSortDirection,
  getStoredFilters,
  includesNormalized,
  saveFilters,
  sortRows,
} from '../../../../utils/tableUtils';

const FILTERS_STORAGE_KEY = 'admin-admin-filters';
const INITIAL_FILTERS = {
  name: '',
  country: '',
};
const SORT_FIELDS = {
  NAME: 'name',
  LAST_NAME: 'last_name',
};
const SORT_COMPARATORS = {
  [SORT_FIELDS.NAME]: (a, b) => compareText(a.name, b.name),
  [SORT_FIELDS.LAST_NAME]: (a, b) => compareText(a.last_name, b.last_name),
};

function TableAdmin({reload, onDataLoaded, showSummary = true, onSummaryChange = () => {}}) {
  const {t} = useTranslation();
  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;

  const [dataLoaded, setDataLoaded] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const [order, setOrder] = useState('');
  const [orderBy, setOrderBy] = useState('');

  const getAdminCountryCodes = (admin) => {
    const managedCodes = (admin?.managed_area_codes || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);

    if (managedCodes.length > 0) {
      return managedCodes;
    }

    return admin?.area_code ? [admin.area_code] : [];
  };

  const handleRequestSort = (property) => {
    setOrder(getNextSortDirection(orderBy, order, property));
    setOrderBy(property);
  };

  const sortData = (array) => {
    return sortRows(array, orderBy, order, SORT_COMPARATORS);
  };

  const navigate = useNavigate();

  const fetchData = async () => {
    const authToken = token;
    const url = `${process.env.REACT_APP_API_URL}/admin/admins`;

    const config = {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Cache-Control': 'no-cache',
      },
    };

    try {
      const response = await apiClient.get(url, config);
      if (response.status === 200) {
        const data = response.data;
        // Obtener los códigos de área únicos
        const uniqueCountries = [
          ...new Set(
            response.data.flatMap((admin) => getAdminCountryCodes(admin))
          )
        ];
        setCountries(orderCountries(uniqueCountries));
        const initialFilters = getStoredFilters(FILTERS_STORAGE_KEY, INITIAL_FILTERS);
        setFilters(initialFilters);
        setAdmins(data);
        applyFilters(initialFilters, data);
        setDataLoaded(true);
        onDataLoaded();

      } else if (response.status === 204) {
        handleEmptyTableResponse({
          clearStateSetters: [setAdmins, setFilteredAdmins, setCountries],
          onDataLoaded,
          onSummaryChange,
          setDataLoaded,
        });
      }
    } catch (error) {
      if (error.response) {
        setSnackbarOpen(true);
        setSnackbarMessage(t('common-internal-server-error'));
      }
    }
  };

  useEffect(() => {
    if (reload) {
      fetchData();
    }
    // eslint-disable-next-line
  }, [reload, token, onDataLoaded, t]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const applyFilters = (appliedFilters = filters, adminsData = admins) => {
    let filtered = [...adminsData];

    if (appliedFilters.name) {
      filtered = filtered.filter(
        (admin) =>
          includesNormalized(admin.name, appliedFilters.name) ||
          includesNormalized(admin.last_name, appliedFilters.name)
      );
    }

    if (appliedFilters.country) {
      filtered = filtered.filter((admin) => {
        const countryCodes = getAdminCountryCodes(admin);
        return countryCodes.includes(appliedFilters.country);
      });
    }

    setFilteredAdmins(filtered);
    onSummaryChange({ total: filtered.length });
    saveFilters(FILTERS_STORAGE_KEY, appliedFilters);
  };

  const handleApplyFilter = () => {
    applyFilters(filters, admins);
  };

  const resetFilters = () => {
    const initialFilters = INITIAL_FILTERS;

    setFilters(initialFilters);
    saveFilters(FILTERS_STORAGE_KEY, initialFilters);

    applyFilters(initialFilters, admins);
  };

  const orderCountries = (countries) => {
    return countryLabelOptions(countries, t);
  };

  return (
    <>
      {dataLoaded ? (
        admins && admins.length > 0 ? (
          <>
            <div className="filters">
              <p>{t('common-search')}</p>

              <Grid container spacing={3}>
                <Grid size={{xs: 12, sm: 6, md: 3}}>
                  <div className="form-control">
                    <label htmlFor="name">
                      {t('common-name')}/{t('common-last-name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={filters.name}
                      onChange={handleFilterChange}
                    />
                  </div>
                </Grid>
                {(role === 'admin' || role === 'marketAdmin') && countries.length > 1 && (
                  <Grid size={{xs: 12, sm: 6, md: 2}}>
                    <div className="form-control">
                      <label htmlFor="country">{t('common-country')}</label>
                      <select
                        name="country"
                        id="country"
                        className="normal"
                        value={filters.country}
                        onChange={handleFilterChange}
                      >
                        <option value="">{t('common-all-country')}</option>
                        {countries.map((country) => (
                          <option key={country.value} value={country.value}>
                            {country.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Grid>
                )}
              </Grid>
              {showSummary && (
                <TableResultsSummary count={filteredAdmins.length} />
              )}
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                  <div className="actions">
                    <button className="btn" onClick={handleApplyFilter}>
                      {t('common-apply')}
                    </button>
                  </div>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <div className="actions">
                    <button onClick={resetFilters} className="btn text narrow">{t('common-reset-filters')}</button>
                  </div>
                </Grid>
              </Grid>
            </div>
            <div className="table-wrapper">
              {filteredAdmins.length !== 0 ? (
                <Table aria-label="simple table" className="user-table admin-admins">
                  <TableHead>
                    <TableRow>
                      <SortableHeaderCell
                        field={SORT_FIELDS.NAME}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-name')}
                      />
                      <SortableHeaderCell
                        field={SORT_FIELDS.LAST_NAME}
                        orderBy={orderBy}
                        order={order}
                        onRequestSort={handleRequestSort}
                        label={t('common-last-name')}
                      />
                      <TableCell align="left"><span className="text">{t('common-country')}</span></TableCell>
                      <TableCell align="left"><span className="text">{t('common-admin-role')}</span></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortData(filteredAdmins).map((row) => (
                      <TableRow key={row.id}>
                        <TableCell align="left"><span className="text">{row.name}</span></TableCell>
                        <TableCell align="left"><span className="text">{row.last_name}</span></TableCell>
                        <TableCell align="left"><span
                          className="text">{t(`countries-${row.area_code}`)}</span></TableCell>
                        <TableCell align="left"><span className="text">{row.role}</span></TableCell>
                        <TableCell align="right">
                          <RowActionsMenu
                            actions={[
                              {
                                key: 'edit-admin',
                                label: t('admin-edit-admin-title'),
                                onClick: () => navigate(`/admin/edit-admin`, {
                                  state: {
                                    id: row.id,
                                    breadcrumbName: `${row.name} ${row.last_name}`,
                                  },
                                }),
                              },
                              {
                                key: 'delete-user',
                                label: t('admin-main-tab2-option3'),
                                onClick: () => navigate(`/admin/delete-user`, {
                                  state: {
                                    user: row,
                                  },
                                }),
                              },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="no-result">
                  <h4 className="txt-center">{t('admin-main-title-empty')}</h4>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="no-result">
            <h2 className="txt-center">
              {t('admin-main-tab1-title-empty')}
              <small>{t('admin-main-tab1-text-empty')}</small>
            </h2>
          </div>
        )
      ) : (
        <div>{t('common-loading')}</div>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert severity="error" sx={{width: '100%'}}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default TableAdmin;
